import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { getFinanceRequests } from '../../../shared/services/data/finance-request.data';
import { hasAccess } from '../../../shared/services/helpers/auth-helper/auth-helper.service';
import {
  FinanceRequestStatus,
  FinanceRequestType,
  FinanceRequestView
} from '../../../shared/models/finance-request.model';
import { FinanceRequestUiModel } from '../../../shared/ui-models/finance-request.ui-model';
import { LoadingData } from '../../../ui/LoadingData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import {
  ADVANCE_STATE,
  BTN_PRIMARY,
  BTN_SECONDARY,
  CHIP,
  CHIP_COLOR,
  CONTENT,
  detailsPath,
  EMPTY,
  fmtDate,
  INPUT,
  LABEL,
  LINK,
  listPath,
  money,
  newPath,
  PAGE,
  PAGE_TITLE,
  OVERSIGHT_PERMISSIONS,
  SECTION_TITLE,
  STATUS,
  TABLE_WRAP,
  TD,
  TH,
  today,
  TYPES
} from '../../shared/finance-request.util';
import { RequestSelect, SelectOption } from '../../shared/request-select.component';
import { ReviewersModal } from '../../shared/reviewers-modal.component';

const TABS: { key: FinanceRequestView; label: string }[] = [
  { key: 'mine', label: 'My requests' },
  { key: 'approve', label: 'To approve' },
  { key: 'register', label: 'Register' }
];

const TYPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'All forms' },
  ...(Object.keys(TYPES) as FinanceRequestType[]).map((k) => ({ value: k, label: TYPES[k].short }))
];
const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  ...(Object.keys(STATUS) as FinanceRequestStatus[])
    .filter((k) => k !== 'draft')
    .map((k) => ({ value: k, label: STATUS[k].label }))
];

export const RequestsListContainer: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const canOversee = hasAccess(OVERSIGHT_PERMISSIONS);
  const requested = new URLSearchParams(location.search).get('tab') as FinanceRequestView | null;
  const tab: FinanceRequestView =
    requested === 'approve' || (requested === 'register' && canOversee) ? requested : 'mine';

  const [rows, setRows] = useState<FinanceRequestUiModel[]>([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [reviewersOpen, setReviewersOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const query =
      tab === 'register'
        ? {
            view: tab,
            type: (type || undefined) as FinanceRequestType | undefined,
            status: (status || undefined) as FinanceRequestStatus | undefined,
            q: q || undefined
          }
        : { view: tab };
    return Promise.all([
      getFinanceRequests(query),
      tab === 'approve' ? Promise.resolve(null) : getFinanceRequests({ view: 'approve' })
    ]).then(([result, approve]) => {
      setLoadError(isErrorInfo(result) ? describeLoadError(result) : undefined);
      if (!isErrorInfo(result)) {
        setRows(result);
        if (tab === 'approve') setPending(result.length);
      }
      if (approve && !isErrorInfo(approve)) setPending(approve.length);
      setLoading(false);
    });
  }, [tab, type, status, q]);

  useEffect(() => {
    const timer = setTimeout(load, tab === 'register' ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, tab]);

  const toSettle = useMemo(
    () =>
      tab === 'mine'
        ? rows.filter(
            (r) => r.type === 'ADV' && r.status === 'approved' && r.advanceState !== 'settled'
          )
        : [],
    [rows, tab]
  );

  const tabs = TABS.filter((t) => t.key !== 'register' || canOversee);

  return (
    <div className={PAGE}>
      <div className={`${CONTENT} pt-[32px] pb-[24px]`}>
        <div className="flex flex-col justify-between gap-[16px] md:flex-row md:items-center">
          <h1 className={PAGE_TITLE}>Advances &amp; settlements</h1>
          <div className="flex shrink-0 flex-wrap items-center gap-[6px]">
            {canOversee ? (
              <button
                type="button"
                className={BTN_SECONDARY}
                onClick={() => setReviewersOpen(true)}
              >
                Reviewers
              </button>
            ) : null}
            <button
              type="button"
              className={BTN_SECONDARY}
              onClick={() => history.push(newPath('STL'))}
            >
              New settlement
            </button>
            <button
              type="button"
              className={BTN_PRIMARY}
              onClick={() => history.push(newPath('ADV'))}
            >
              New advance request
            </button>
          </div>
        </div>
      </div>

      {reviewersOpen ? (
        <ReviewersModal
          onClose={() => setReviewersOpen(false)}
          onSaved={() => {
            setReviewersOpen(false);
            load();
          }}
        />
      ) : null}

      <nav className="sticky top-0 z-40 border-b border-gray-200 bg-neutral-0">
        <div className={`${CONTENT} flex gap-[28px] overflow-x-auto [scrollbar-width:none]`}>
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => history.push(listPath(t.key))}
              className={`flex items-center gap-[8px] whitespace-nowrap border-b-2 py-[16px] text-[14px] font-gilroy-medium ${
                tab === t.key
                  ? 'border-neutral-500 text-neutral-500'
                  : 'border-transparent text-neutral-300 hover:text-neutral-500'
              }`}
            >
              {t.label}
              {t.key === 'approve' && pending > 0 ? (
                <span className="grid h-[20px] min-w-[20px] place-items-center rounded-full bg-primary-600 px-[4px] text-[10px] font-gilroy-bold text-neutral-0">
                  {pending}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </nav>

      <div className={`${CONTENT} flex flex-col gap-[32px] pt-[32px] pb-[80px]`}>
        {toSettle.length > 0 ? (
          <section>
            <h2 className={SECTION_TITLE}>Advances to settle</h2>
            <div className={TABLE_WRAP}>
              <table className="w-full min-w-[720px] border-collapse">
                <thead className="bg-creams-100">
                  <tr>
                    {['Advance', 'Purpose', 'Amount', 'Due', ''].map((c) => (
                      <th
                        key={c || 'action'}
                        className={`${TH} ${c === 'Amount' ? 'text-right' : ''}`}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {toSettle.map((a) => {
                    const due = a.totals.settlementDue;
                    const late = a.advanceState === 'open' && !!due && due < today();
                    return (
                      <tr key={a.id} className="bg-neutral-0">
                        <td className={`${TD} font-gilroy-medium`}>
                          <button
                            type="button"
                            className={LINK}
                            onClick={() => history.push(detailsPath(a.id))}
                          >
                            {a.serial}
                          </button>
                        </td>
                        <td className={`${TD} max-w-[240px] truncate`}>{a.form.purpose}</td>
                        <td className={`${TD} whitespace-nowrap text-right`}>
                          {money(a.totals.requested, a.form.currency)}
                        </td>
                        <td className={`${TD} whitespace-nowrap`}>
                          {late ? (
                            <span className={`${CHIP} ${CHIP_COLOR.LATE}`}>
                              Overdue · {fmtDate(due)}
                            </span>
                          ) : (
                            <span className="text-neutral-300">{fmtDate(due)}</span>
                          )}
                        </td>
                        <td className={`${TD} text-right`}>
                          {a.advanceState === 'open' ? (
                            <button
                              type="button"
                              className={BTN_SECONDARY}
                              onClick={() => history.push(newPath('STL', a.id))}
                            >
                              Settle
                            </button>
                          ) : (
                            <span className={`${CHIP} ${ADVANCE_STATE.settling.chip}`}>
                              {ADVANCE_STATE.settling.label}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {tab === 'register' ? (
          <section className="grid grid-cols-2 gap-[12px] md:grid-cols-4">
            <label className={`${LABEL} col-span-2`}>
              Search
              <input
                type="search"
                className={INPUT}
                placeholder="Number, employee, purpose"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
            <div className={LABEL}>
              Form
              <RequestSelect
                ariaLabel="Form"
                value={type}
                options={TYPE_OPTIONS}
                onChange={setType}
              />
            </div>
            <div className={LABEL}>
              Status
              <RequestSelect
                ariaLabel="Status"
                value={status}
                options={STATUS_OPTIONS}
                onChange={setStatus}
              />
            </div>
          </section>
        ) : null}

        <section>
          {tab === 'mine' && toSettle.length > 0 ? (
            <h2 className={SECTION_TITLE}>All requests</h2>
          ) : null}
          {loading ? (
            <LoadingData />
          ) : loadError ? (
            <LoadError message={loadError} onRetry={load} />
          ) : rows.length === 0 ? (
            <div className={EMPTY}>
              {tab === 'approve'
                ? 'Nothing waiting for you'
                : tab === 'register'
                ? 'No submitted requests'
                : 'No requests yet'}
            </div>
          ) : (
            <div className={TABLE_WRAP}>
              <table className="w-full min-w-[860px] border-collapse">
                <thead className="bg-creams-100">
                  <tr>
                    <th className={TH}>Number</th>
                    <th className={TH}>Form</th>
                    {tab !== 'mine' ? <th className={TH}>Employee</th> : null}
                    <th className={TH}>Purpose</th>
                    <th className={TH}>Submitted</th>
                    <th className={`${TH} text-right`}>Amount</th>
                    <th className={TH}>Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      tabIndex={0}
                      className="cursor-pointer bg-neutral-0 transition-colors hover:bg-creams-100"
                      onClick={() => history.push(detailsPath(r.id))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') history.push(detailsPath(r.id));
                      }}
                    >
                      <td className={`${TD} whitespace-nowrap font-gilroy-medium`}>
                        {r.serial || <span className="text-neutral-300">Draft</span>}
                      </td>
                      <td className={`${TD} whitespace-nowrap`}>{TYPES[r.type].short}</td>
                      {tab !== 'mine' ? (
                        <td className={`${TD} whitespace-nowrap`}>{r.employee.name}</td>
                      ) : null}
                      <td className={`${TD} max-w-[240px] truncate text-neutral-300`}>
                        {r.form.purpose || '—'}
                      </td>
                      <td className={`${TD} whitespace-nowrap text-neutral-300`}>
                        {fmtDate(r.submittedAt)}
                      </td>
                      <td className={`${TD} whitespace-nowrap text-right`}>
                        {money(r.amount, r.form.currency)}
                      </td>
                      <td className={TD}>
                        <span className={`${CHIP} ${STATUS[r.status].chip}`}>
                          {STATUS[r.status].label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
